// Zustand store para la pantalla NutricionistaReportes (Sprint 6 Lote 2 T6).
// Mantiene los filtros activos (sede + ventana) + la ultima carga por tab,
// para que el usuario pueda cambiar de tab sin perder los filtros ni los
// resultados ya cargados (UX de pantalla de reportes con multiples vistas).
import { create } from 'zustand'
import {
  reportesNutriApi,
  type AdherenciaPacienteResponse,
  type AlergiasPeriodoResponse,
  type IngestaPacienteRow,
  type ReporteKind,
  type SustitucionesPeriodoResponse,
} from '../services/reportesNutri'

export type TabReporteKey = ReporteKind

export interface FiltrosReportes {
  sede_id: number | null
  semana_inicio: string // YYYY-MM-DD
  paciente_id: number | null
  fecha_inicio: string
  fecha_fin: string
}

function isoDateAddDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function lunesActualISO(): string {
  // Calcula el lunes de la semana en curso en UTC (alineado con backend).
  const hoy = new Date()
  const dow = hoy.getUTCDay() // 0=domingo .. 6=sabado
  const desplazamiento = (dow + 6) % 7 // dias desde el lunes
  const lunes = new Date(hoy)
  lunes.setUTCDate(hoy.getUTCDate() - desplazamiento)
  return lunes.toISOString().slice(0, 10)
}

interface ReportesNutriState {
  tabActivo: TabReporteKey
  filtros: FiltrosReportes
  loading: boolean
  error: string | null

  // Datos por tab — se conservan al cambiar de tab.
  ingesta: IngestaPacienteRow[] | null
  adherencia: AdherenciaPacienteResponse | null
  alergias: AlergiasPeriodoResponse | null
  sustituciones: SustitucionesPeriodoResponse | null

  setTabActivo: (tab: TabReporteKey) => void
  setFiltros: (parcial: Partial<FiltrosReportes>) => void

  cargarIngesta: () => Promise<void>
  cargarAdherencia: () => Promise<void>
  cargarAlergias: () => Promise<void>
  cargarSustituciones: () => Promise<void>

  exportarActual: () => Promise<{ filename: string; blob: Blob }>
}

function extractError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  const maybe = err as { response?: { data?: { detail?: unknown } } }
  const detail = maybe.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const obj = detail as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
  }
  return 'Error desconocido al consultar reportes'
}

const HOY = new Date().toISOString().slice(0, 10)

export const useReportesNutriStore = create<ReportesNutriState>((set, get) => ({
  tabActivo: 'ingesta',
  filtros: {
    sede_id: null,
    semana_inicio: lunesActualISO(),
    paciente_id: null,
    fecha_inicio: isoDateAddDays(HOY, -6),
    fecha_fin: HOY,
  },
  loading: false,
  error: null,

  ingesta: null,
  adherencia: null,
  alergias: null,
  sustituciones: null,

  setTabActivo: (tab) => set({ tabActivo: tab, error: null }),

  setFiltros: (parcial) =>
    set((s) => ({ filtros: { ...s.filtros, ...parcial } })),

  cargarIngesta: async () => {
    const { sede_id, semana_inicio } = get().filtros
    set({ loading: true, error: null })
    try {
      const data = await reportesNutriApi.ingestaSemanal({
        sede_id,
        semana_inicio,
      })
      set({ ingesta: data, loading: false })
    } catch (err) {
      set({ error: extractError(err), loading: false })
    }
  },

  cargarAdherencia: async () => {
    const { paciente_id, semana_inicio } = get().filtros
    if (!paciente_id) {
      set({ error: 'paciente_id es obligatorio para adherencia' })
      return
    }
    set({ loading: true, error: null })
    try {
      const data = await reportesNutriApi.adherencia(paciente_id, {
        semana_inicio,
      })
      set({ adherencia: data, loading: false })
    } catch (err) {
      set({ error: extractError(err), loading: false })
    }
  },

  cargarAlergias: async () => {
    const { sede_id, fecha_inicio, fecha_fin } = get().filtros
    set({ loading: true, error: null })
    try {
      const data = await reportesNutriApi.alergias({
        sede_id,
        fecha_inicio,
        fecha_fin,
      })
      set({ alergias: data, loading: false })
    } catch (err) {
      set({ error: extractError(err), loading: false })
    }
  },

  cargarSustituciones: async () => {
    const { sede_id, fecha_inicio, fecha_fin } = get().filtros
    set({ loading: true, error: null })
    try {
      const data = await reportesNutriApi.sustituciones({
        sede_id,
        fecha_inicio,
        fecha_fin,
      })
      set({ sustituciones: data, loading: false })
    } catch (err) {
      set({ error: extractError(err), loading: false })
    }
  },

  exportarActual: async () => {
    const { tabActivo, filtros } = get()
    return reportesNutriApi.exportCsv({
      kind: tabActivo,
      sede_id: filtros.sede_id,
      semana_inicio:
        tabActivo === 'ingesta' || tabActivo === 'adherencia'
          ? filtros.semana_inicio
          : null,
      paciente_id: tabActivo === 'adherencia' ? filtros.paciente_id : null,
      fecha_inicio:
        tabActivo === 'alergias' || tabActivo === 'sustituciones'
          ? filtros.fecha_inicio
          : null,
      fecha_fin:
        tabActivo === 'alergias' || tabActivo === 'sustituciones'
          ? filtros.fecha_fin
          : null,
    })
  },
}))
